// Cancel an order from the kitchen display (spec/06 §8.2, spec/13 §6.2, F-POS-15). A ticket names its order;
// the dialog reads that order so staff see every line — an order may have a ticket at the bar and one in the
// kitchen — and its total before they cancel. Cancelling is irreversible, so it states the consequence and
// confirms (spec/13 §2).
//
// Two kinds of order reach the board. A counter or kiosk order is a **sale** (F-KIO-10, F-FNB-04): it is read
// with `getSale` and cancelled with `cancelSale` while it has no payment. An older order is a **food order**
// (`getFoodOrder` / `cancelFoodOrder`), cancellable while placed and unsettled. A paid order of either kind is
// refunded from Orders instead, and the dialog says so rather than offering a button that would fail.

import type { FoodOrder, Sale } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, Skeleton, useToast } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { LoadFailure } from '../../shell/LoadFailure';
import { Facts, Notice, Section } from '../../ui';
import { useCancelSale, useSale } from '../orders/queries';
import { formatMoney } from '../sessions/money';
import { kitchenKeys, useCancelFoodOrder, useFoodOrder } from './queries';

/** The order a ticket's Cancel opens: its sale, or its (older) food order, with the tag the board shows. */
export interface CancelTarget {
  readonly kind: 'sale' | 'food_order';
  readonly id: string;
  /** The order's number or short tag, e.g. `#0421`. */
  readonly tag: string;
}

/** Whether the contract lets this food order be cancelled: placed, and not yet settled. */
export function isCancellable(order: Pick<FoodOrder, 'status' | 'settlementStatus'>): boolean {
  return order.status === 'placed' && order.settlementStatus === 'unsettled';
}

/** Whether a sale can be cancelled: placed with nothing paid on it (`cancelSale`'s `409` otherwise). */
export function isSaleCancellable(sale: Pick<Sale, 'status' | 'paid'>): boolean {
  return sale.status === 'placed' && sale.paid.amount === 0;
}

export function CancelOrderDialog({
  organizationId,
  venueId,
  target,
  onClose,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  /** The order to cancel; `null` keeps the dialog closed. */
  readonly target: CancelTarget | null;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => (open ? undefined : onClose())}
      eyebrow={t('kitchen.cancel.eyebrow')}
      title={t('kitchen.cancel.title', { order: target?.tag ?? '' })}
      size="sm"
    >
      {target === null ? null : target.kind === 'sale' ? (
        <CancelSaleBody organizationId={organizationId} venueId={venueId} saleId={target.id} onDone={onClose} />
      ) : (
        <CancelFoodOrderBody organizationId={organizationId} venueId={venueId} orderId={target.id} onDone={onClose} />
      )}
    </Dialog>
  );
}

interface OrderSummary {
  readonly lines: readonly { readonly id: string; readonly quantity: number; readonly name: string; readonly total: FoodOrder['total'] }[];
  readonly total: FoodOrder['total'];
  readonly channel: FoodOrder['channel'];
  readonly cancellable: boolean;
  readonly cancelled: boolean;
}

/** The lines, the total, and the confirm — the same for a sale and a food order. */
function CancelBody({
  order,
  busy,
  onCancel,
  error,
}: {
  readonly order: OrderSummary;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly error: string | undefined;
}): ReactNode {
  const { t } = useTranslation('console');
  const [confirming, setConfirming] = useState(false);
  return (
    <div className="kitchen-cancel">
      <Section title={t('kitchen.cancel.lines')} titleAs="h3" body="rows">
        <ul className="kitchen-cancel__lines">
          {order.lines.map((line) => (
            <li key={line.id} className="kitchen-cancel__line">
              <span className="console-num">{line.quantity}×</span>
              <span className="kitchen-cancel__name">{line.name}</span>
              <span className="console-num">{formatMoney(line.total)}</span>
            </li>
          ))}
        </ul>
      </Section>
      <Facts
        items={[
          { key: 'total', label: t('kitchen.cancel.total'), value: <span className="console-num">{formatMoney(order.total)}</span> },
          { key: 'channel', label: t('kitchen.cancel.channel'), value: t(`kitchen.cancel.channels.${order.channel}`) },
        ]}
      />

      {error === undefined ? null : <Notice tone="danger" role="alert" title={error} />}

      {!order.cancellable ? (
        <Notice role="note" title={order.cancelled ? t('kitchen.cancel.alreadyCancelled') : t('kitchen.cancel.settled')} />
      ) : confirming ? (
        <Notice
          tone="warn"
          role="alert"
          title={t('kitchen.cancel.confirm')}
          action={
            <>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                {t('kitchen.cancel.keep')}
              </Button>
              <Button
                variant="danger"
                className="clav-button--danger-solid"
                size="sm"
                busy={busy}
                onClick={onCancel}
              >
                {t('kitchen.cancel.action')}
              </Button>
            </>
          }
        />
      ) : (
        <div className="console-form__actions">
          <Button variant="danger" onClick={() => setConfirming(true)}>
            {t('kitchen.cancel.action')}
          </Button>
        </div>
      )}
    </div>
  );
}

function Loading({ error, retry }: { readonly error: unknown; readonly retry: (() => void) | undefined }): ReactNode {
  const { t } = useTranslation('console');
  return retry !== undefined ? <LoadFailure error={error} message={t('kitchen.cancel.unavailable')} onRetry={retry} /> : <Skeleton height="10rem" />;
}

function CancelFoodOrderBody({
  organizationId,
  venueId,
  orderId,
  onDone,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly orderId: string;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const order = useFoodOrder(organizationId, venueId, orderId);
  const cancel = useCancelFoodOrder(organizationId, venueId);
  const [error, setError] = useState<string | undefined>(undefined);

  if (order.data === undefined) return <Loading error={order.error} retry={order.isError ? () => void order.refetch() : undefined} />;
  const data = order.data;
  return (
    <CancelBody
      order={{ lines: data.lines, total: data.total, channel: data.channel, cancellable: isCancellable(data), cancelled: data.status === 'cancelled' }}
      busy={cancel.isPending}
      error={error}
      onCancel={() => {
        setError(undefined);
        cancel.mutate(
          { orderId },
          {
            onSuccess: () => {
              show({ tone: 'ok', title: t('kitchen.cancel.done') });
              onDone();
            },
            onError: (failure) => setError(problemCode(failure) === PROBLEM.conflict ? t('kitchen.cancel.conflict') : problem.text(failure, { fallback: t('kitchen.cancel.failed') })),
          },
        );
      }}
    />
  );
}

function CancelSaleBody({
  organizationId,
  venueId,
  saleId,
  onDone,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly saleId: string;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const sale = useSale(organizationId, venueId, saleId, true);
  const cancel = useCancelSale(organizationId, venueId, saleId);
  const [error, setError] = useState<string | undefined>(undefined);

  if (sale.data === undefined) return <Loading error={sale.error} retry={sale.isError ? () => void sale.refetch() : undefined} />;
  const data = sale.data;
  return (
    <CancelBody
      order={{ lines: data.lines, total: data.total, channel: data.channel, cancellable: isSaleCancellable(data), cancelled: data.status === 'cancelled' }}
      busy={cancel.isPending}
      error={error}
      onCancel={() => {
        setError(undefined);
        cancel.mutate(undefined, {
          onSuccess: () => {
            // The server cancels the sale's tickets after the commit; re-read the board so they leave it.
            void queryClient.invalidateQueries({ queryKey: kitchenKeys.tickets(organizationId, venueId, 'active') });
            show({ tone: 'ok', title: t('kitchen.cancel.done') });
            onDone();
          },
          onError: (failure) => setError(problemCode(failure) === PROBLEM.conflict ? t('kitchen.cancel.conflict') : problem.text(failure, { fallback: t('kitchen.cancel.failed') })),
        });
      }}
    />
  );
}
